import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X, Upload, Sparkles, Sofa, Bed, Armchair, Layers, Square, HelpCircle, Trash2, Phone, Mail, Loader2, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO, startOfDay } from "date-fns";
import { pt } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Logo } from "@/components/Logo";
import WhatsAppFAB from "@/components/WhatsAppFAB";

const ICONS = { sofa: Sofa, colchao: Bed, cadeira: Armchair, carpete: Layers, poltrona: Armchair, alcatifa: Square, outro: HelpCircle };

const STEPS = [
    "Serviço",
    "Características",
    "Estado",
    "Localização",
    "Fotografias",
    "Dados",
    "Resumo",
];

const CONDITIONS = [
    { key: "sujidade_elevada", label: "Sujidade elevada" },
    { key: "manchas", label: "Manchas" },
    { key: "odores", label: "Odores" },
    { key: "pelos_animais", label: "Pelos de animais" },
    { key: "urina_animais", label: "Urina de animais" },
    { key: "vomito", label: "Vómito" },
    { key: "humidade_bolor", label: "Humidade ou bolor" },
    { key: "acaros_alergias", label: "Ácaros e alergias" },
];

const DISTRICTS = ["Aveiro", "Beja", "Braga", "Bragança", "Castelo Branco", "Coimbra", "Évora", "Faro", "Guarda", "Leiria", "Lisboa", "Portalegre", "Porto", "Santarém", "Setúbal", "Viana do Castelo", "Vila Real", "Viseu", "Açores", "Madeira"];

export default function Calculator() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // wizard state
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [itemDetails, setItemDetails] = useState({}); // { sofa: {...}, colchao: {...} }
    const [conditions, setConditions] = useState([]);
    const [disinfection, setDisinfection] = useState(false);
    const [impermeabilization, setImpermeabilization] = useState(false);
    const [location, setLocation] = useState({ postal_code: "", locality: "", district: "", address: "", floor: "", elevator: "nao_sei", parking: "nao_sei" });
    const [photos, setPhotos] = useState([]); // [{path, name}]
    const [uploading, setUploading] = useState(false);
    const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
    const [preferredDate, setPreferredDate] = useState("");
    const [preferredPeriod, setPreferredPeriod] = useState("indiferente");
    const [notes, setNotes] = useState("");
    const [promoCode, setPromoCode] = useState("");
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [calc, setCalc] = useState(null);
    const [submittedQuote, setSubmittedQuote] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/services");
                setServices(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const selectedServices = useMemo(() => services.filter((s) => selectedKeys.includes(s.key)), [services, selectedKeys]);

    const toggleService = (key) => {
        setSelectedKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
        setItemDetails((prev) => ({ ...prev, [key]: prev[key] || defaultDetails(key) }));
    };

    function defaultDetails(key) {
        switch (key) {
            case "sofa": return { seats: 3, chaise_longue: false, sofa_cama: false, almofadas_soltas: false, num_almofadas: 0 };
            case "colchao": return { tipo: "casal", duas_faces: false, cabeceira: false, quantity: 1 };
            case "cadeira": return { quantity: 4, tipo: "assento_costas" };
            case "carpete": return { length: 2, width: 3, material: "" };
            case "poltrona": return { quantity: 1, apoio_pes: false, almofadas_soltas: false };
            case "alcatifa": return { divisions: [{ length: 3, width: 4, name: "Sala" }] };
            case "outro": return { description: "", quantity: 1, measurements: "" };
            default: return {};
        }
    }

    const buildItems = () => selectedKeys.map((key) => {
        const d = itemDetails[key] || {};
        let quantity = 1;
        if (key === "colchao" || key === "cadeira" || key === "poltrona" || key === "outro") quantity = d.quantity || 1;
        return { service_key: key, quantity, details: d };
    });

    const recalc = async () => {
        try {
            const { data } = await api.post("/quotes/calculate", {
                items: buildItems(),
                conditions,
                disinfection,
                impermeabilization,
                location,
                promo_code: promoCode || null,
            });
            setCalc(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        if (step === 6 && selectedKeys.length > 0) recalc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const canNext = () => {
        if (step === 0) return selectedKeys.length > 0;
        if (step === 3) return location.postal_code && location.locality && location.district && (location.address || "").trim();
        if (step === 4) {
            if (selectedKeys.includes("outro")) return photos.length > 0;
            return true;
        }
        if (step === 5) return customer.name && customer.phone && customer.email && privacyAccepted;
        return true;
    };

    const goNext = () => {
        if (!canNext()) {
            toast.error("Preencha os campos obrigatórios para continuar.");
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const goPrev = () => {
        setStep((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const uploadFile = async (file) => {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        return { path: data.path, name: file.name };
    };

    const handleUpload = async (files) => {
        setUploading(true);
        try {
            for (const file of files) {
                if (file.size > 10 * 1024 * 1024) {
                    toast.error(`${file.name} demasiado grande (máx 10MB)`);
                    continue;
                }
                const p = await uploadFile(file);
                setPhotos((prev) => [...prev, p]);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const submitQuote = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/quotes", {
                items: buildItems(),
                conditions,
                disinfection,
                impermeabilization,
                location,
                photos: photos.map((p) => p.path),
                customer,
                preferred_date: preferredDate || null,
                preferred_period: preferredPeriod,
                notes,
                promo_code: promoCode || null,
                privacy_accepted: privacyAccepted,
            });
            setSubmittedQuote(data);
            toast.success("Pedido enviado com sucesso!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (submittedQuote) {
        return <SubmittedView quote={submittedQuote} onNew={() => window.location.reload()} onHome={() => navigate("/")} />;
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white">
            <header className="ab-glass-header sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="back-home">
                        <Logo variant="full" size={44} />
                    </Link>
                    <div className="text-xs text-white/60 font-medium hidden sm:block uppercase tracking-widest">
                        Passo <span className="text-[#C9A961] font-bold">{step + 1}</span> de {STEPS.length}
                    </div>
                </div>
                <div className="max-w-6xl mx-auto px-6 pb-4 flex items-center gap-2">
                    {STEPS.map((label, i) => (
                        <div key={i} className="flex-1 flex flex-col items-start gap-1.5">
                            <div className="h-0.5 w-full bg-[#2A2A2A] overflow-hidden">
                                <div className={`h-full transition-all duration-500 ${i <= step ? "bg-[#C9A961]" : ""}`} style={{ width: i <= step ? "100%" : "0%" }} />
                            </div>
                            <div className={`text-[10px] font-medium uppercase tracking-widest hidden md:block ${i === step ? "text-[#C9A961]" : "text-white/40"}`}>
                                {label}
                            </div>
                        </div>
                    ))}
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 pt-10 pb-28 lg:py-14">
                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
                ) : (
                    <div className="grid lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-8">
                            <div className="ab-fade-up" key={step}>
                                {step === 0 && <StepService services={services} selectedKeys={selectedKeys} onToggle={toggleService} />}
                                {step === 1 && <StepCharacteristics selectedKeys={selectedKeys} services={services} itemDetails={itemDetails} setItemDetails={setItemDetails} />}
                                {step === 2 && <StepConditions conditions={conditions} setConditions={setConditions} disinfection={disinfection} setDisinfection={setDisinfection} impermeabilization={impermeabilization} setImpermeabilization={setImpermeabilization} />}
                                {step === 3 && <StepLocation location={location} setLocation={setLocation} />}
                                {step === 4 && <StepPhotos photos={photos} setPhotos={setPhotos} onUpload={handleUpload} uploading={uploading} required={selectedKeys.includes("outro")} />}
                                {step === 5 && <StepCustomer customer={customer} setCustomer={setCustomer} preferredDate={preferredDate} setPreferredDate={setPreferredDate} preferredPeriod={preferredPeriod} setPreferredPeriod={setPreferredPeriod} notes={notes} setNotes={setNotes} promoCode={promoCode} setPromoCode={setPromoCode} privacyAccepted={privacyAccepted} setPrivacyAccepted={setPrivacyAccepted} />}
                                {step === 6 && <StepResult calc={calc} onRecalc={recalc} />}
                            </div>
                            <div className="mt-10 flex items-center justify-between">
                                <Button variant="ghost" onClick={goPrev} disabled={step === 0} data-testid="wizard-prev">
                                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                                </Button>
                                {step < STEPS.length - 1 ? (
                                    <Button onClick={goNext} className="ab-btn-primary rounded-full h-12 px-6" data-testid="wizard-next">
                                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                ) : (
                                    <Button onClick={submitQuote} disabled={submitting} className="ab-btn-primary rounded-full h-12 px-6" data-testid="wizard-submit">
                                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                        Enviar pedido de orçamento
                                    </Button>
                                )}
                            </div>
                        </div>

                        <aside className="lg:col-span-4">
                            <div className="sticky top-32 space-y-4">
                                <SummaryCard selectedServices={selectedServices} calc={calc} conditions={conditions} disinfection={disinfection} impermeabilization={impermeabilization} />
                            </div>
                        </aside>
                    </div>
                )}
            </main>
            <WhatsAppFAB message="Olá! Estou a preencher o orçamento no site e tenho uma questão." />
        </div>
    );
}

// ---------------- Step Components ----------------

function StepService({ services, selectedKeys, onToggle }) {
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Que serviços precisa?</h2>
            <p className="mt-2 text-white/60">Pode escolher mais do que uma categoria.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {services.map((s) => {
                    const Icon = ICONS[s.key] || HelpCircle;
                    const selected = selectedKeys.includes(s.key);
                    return (
                        <button
                            key={s.key}
                            onClick={() => onToggle(s.key)}
                            className="ab-service-tile text-left"
                            data-selected={selected}
                            data-testid={`service-${s.key}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="w-11 h-11 rounded-xl bg-[#C9A961]/10 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-[#C9A961]" />
                                </div>
                                {selected && <div className="w-6 h-6 rounded-full bg-[#C9A961] flex items-center justify-center"><Check className="w-4 h-4 text-white" /></div>}
                            </div>
                            <h3 className="mt-4 font-heading font-bold text-lg">{s.name}</h3>
                            <p className="text-sm text-white/50 mt-1">{s.description}</p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function StepCharacteristics({ selectedKeys, services, itemDetails, setItemDetails }) {
    if (selectedKeys.length === 0) {
        return <EmptyState msg="Selecione pelo menos um serviço para continuar." />;
    }
    const update = (key, patch) => setItemDetails((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Características dos artigos</h2>
            <p className="mt-2 text-white/60">Ajude-nos a preparar um orçamento preciso.</p>
            <div className="mt-8 space-y-6">
                {selectedKeys.map((key) => {
                    const svc = services.find((s) => s.key === key);
                    const d = itemDetails[key] || {};
                    const Icon = ICONS[key] || HelpCircle;
                    return (
                        <div key={key} className="ab-card p-6 lg:p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-[#C9A961]/10 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-[#C9A961]" />
                                </div>
                                <h3 className="font-heading font-bold text-xl">{svc?.name}</h3>
                            </div>
                            {key === "sofa" && <SofaFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "colchao" && <ColchaoFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "cadeira" && <CadeiraFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "carpete" && <CarpeteFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "poltrona" && <PoltronaFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "alcatifa" && <AlcatifaFields d={d} onChange={(p) => update(key, p)} />}
                            {key === "outro" && <OutroFields d={d} onChange={(p) => update(key, p)} />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SofaFields({ d, onChange }) {
    return (
        <div className="grid sm:grid-cols-2 gap-6">
            <div>
                <Label>Número de lugares</Label>
                <Input type="number" min={1} max={10} value={d.seats || 3} onChange={(e) => onChange({ seats: Number(e.target.value) })} data-testid="sofa-seats" />
            </div>
            <div className="flex items-center gap-3 pt-6">
                <Checkbox checked={d.chaise_longue} onCheckedChange={(v) => onChange({ chaise_longue: !!v })} id="chaise" data-testid="sofa-chaise" />
                <Label htmlFor="chaise">Com chaise longue</Label>
            </div>
            <div className="flex items-center gap-3">
                <Checkbox checked={d.sofa_cama} onCheckedChange={(v) => onChange({ sofa_cama: !!v })} id="sc" data-testid="sofa-cama" />
                <Label htmlFor="sc">Sofá-cama</Label>
            </div>
            <div className="flex items-center gap-3">
                <Checkbox checked={d.almofadas_soltas} onCheckedChange={(v) => onChange({ almofadas_soltas: !!v })} id="as" data-testid="sofa-almofadas" />
                <Label htmlFor="as">Almofadas soltas</Label>
            </div>
            {d.almofadas_soltas && (
                <div>
                    <Label>Nº de almofadas</Label>
                    <Input type="number" min={0} value={d.num_almofadas || 0} onChange={(e) => onChange({ num_almofadas: Number(e.target.value) })} data-testid="sofa-num-almofadas" />
                </div>
            )}
        </div>
    );
}

function ColchaoFields({ d, onChange }) {
    return (
        <div className="grid sm:grid-cols-2 gap-6">
            <div>
                <Label>Tipo de colchão</Label>
                <Select value={d.tipo || "casal"} onValueChange={(v) => onChange({ tipo: v })}>
                    <SelectTrigger data-testid="colchao-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="solteiro">Solteiro</SelectItem>
                        <SelectItem value="casal">Casal</SelectItem>
                        <SelectItem value="king">King size</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Quantidade</Label>
                <Input type="number" min={1} value={d.quantity || 1} onChange={(e) => onChange({ quantity: Number(e.target.value) })} data-testid="colchao-qty" />
            </div>
            <div className="flex items-center gap-3">
                <Checkbox checked={d.duas_faces} onCheckedChange={(v) => onChange({ duas_faces: !!v })} id="df" data-testid="colchao-duas-faces" />
                <Label htmlFor="df">Limpar duas faces</Label>
            </div>
            <div className="flex items-center gap-3">
                <Checkbox checked={d.cabeceira} onCheckedChange={(v) => onChange({ cabeceira: !!v })} id="cb" data-testid="colchao-cabeceira" />
                <Label htmlFor="cb">Incluir cabeceira</Label>
            </div>
        </div>
    );
}

function CadeiraFields({ d, onChange }) {
    return (
        <div className="grid sm:grid-cols-2 gap-6">
            <div>
                <Label>Quantidade</Label>
                <Input type="number" min={1} value={d.quantity || 1} onChange={(e) => onChange({ quantity: Number(e.target.value) })} data-testid="cadeira-qty" />
            </div>
            <div>
                <Label>Tipo</Label>
                <Select value={d.tipo || "assento_costas"} onValueChange={(v) => onChange({ tipo: v })}>
                    <SelectTrigger data-testid="cadeira-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="assento">Apenas assento</SelectItem>
                        <SelectItem value="assento_costas">Assento e costas</SelectItem>
                        <SelectItem value="totalmente_estofada">Totalmente estofada</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}

function CarpeteFields({ d, onChange }) {
    const area = ((d.length || 0) * (d.width || 0)).toFixed(2);
    return (
        <div className="grid sm:grid-cols-3 gap-6">
            <div>
                <Label>Comprimento (m)</Label>
                <Input type="number" step="0.1" min={0} value={d.length || 0} onChange={(e) => onChange({ length: Number(e.target.value) })} data-testid="carpete-length" />
            </div>
            <div>
                <Label>Largura (m)</Label>
                <Input type="number" step="0.1" min={0} value={d.width || 0} onChange={(e) => onChange({ width: Number(e.target.value) })} data-testid="carpete-width" />
            </div>
            <div>
                <Label>Área calculada</Label>
                <div className="h-10 flex items-center px-3 bg-[#1F1F1F] rounded-md border border-[#2A2A2A] font-heading font-bold text-[#C9A961]">{area} m²</div>
            </div>
            <div className="sm:col-span-3">
                <Label>Material (se souber)</Label>
                <Input placeholder="Lã, algodão, sintético..." value={d.material || ""} onChange={(e) => onChange({ material: e.target.value })} data-testid="carpete-material" />
            </div>
        </div>
    );
}

function PoltronaFields({ d, onChange }) {
    return (
        <div className="grid sm:grid-cols-2 gap-6">
            <div>
                <Label>Quantidade</Label>
                <Input type="number" min={1} value={d.quantity || 1} onChange={(e) => onChange({ quantity: Number(e.target.value) })} data-testid="poltrona-qty" />
            </div>
            <div className="flex items-center gap-3 pt-6">
                <Checkbox checked={d.apoio_pes} onCheckedChange={(v) => onChange({ apoio_pes: !!v })} id="ap" data-testid="poltrona-apoio" />
                <Label htmlFor="ap">Com apoio para os pés</Label>
            </div>
            <div className="flex items-center gap-3">
                <Checkbox checked={d.almofadas_soltas} onCheckedChange={(v) => onChange({ almofadas_soltas: !!v })} id="pas" data-testid="poltrona-almofadas" />
                <Label htmlFor="pas">Almofadas soltas</Label>
            </div>
        </div>
    );
}

function AlcatifaFields({ d, onChange }) {
    const divisions = d.divisions || [];
    const totalArea = divisions.reduce((sum, x) => sum + (Number(x.length || 0) * Number(x.width || 0)), 0);
    const update = (i, patch) => onChange({ divisions: divisions.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
    const add = () => onChange({ divisions: [...divisions, { length: 3, width: 4, name: `Divisão ${divisions.length + 1}` }] });
    const remove = (i) => onChange({ divisions: divisions.filter((_, idx) => idx !== i) });
    return (
        <div>
            <div className="space-y-4">
                {divisions.map((div, i) => (
                    <div key={i} className="grid sm:grid-cols-4 gap-4 items-end p-4 bg-[#1F1F1F] rounded-lg">
                        <div>
                            <Label>Nome</Label>
                            <Input value={div.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`alcatifa-name-${i}`} />
                        </div>
                        <div>
                            <Label>Comprimento (m)</Label>
                            <Input type="number" step="0.1" value={div.length || 0} onChange={(e) => update(i, { length: Number(e.target.value) })} data-testid={`alcatifa-length-${i}`} />
                        </div>
                        <div>
                            <Label>Largura (m)</Label>
                            <Input type="number" step="0.1" value={div.width || 0} onChange={(e) => update(i, { width: Number(e.target.value) })} data-testid={`alcatifa-width-${i}`} />
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`alcatifa-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
                <Button variant="outline" onClick={add} data-testid="alcatifa-add"><Sparkles className="w-4 h-4 mr-2" />Adicionar divisão</Button>
                <div className="font-heading text-[#C9A961] font-bold">Total: {totalArea.toFixed(2)} m²</div>
            </div>
        </div>
    );
}

function OutroFields({ d, onChange }) {
    return (
        <div className="space-y-4">
            <div>
                <Label>Descrição do artigo</Label>
                <Textarea rows={3} value={d.description || ""} onChange={(e) => onChange({ description: e.target.value })} data-testid="outro-desc" placeholder="Descreva o artigo que pretende limpar" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Quantidade</Label>
                    <Input type="number" min={1} value={d.quantity || 1} onChange={(e) => onChange({ quantity: Number(e.target.value) })} data-testid="outro-qty" />
                </div>
                <div>
                    <Label>Medidas aproximadas</Label>
                    <Input value={d.measurements || ""} onChange={(e) => onChange({ measurements: e.target.value })} placeholder="Ex: 2m x 1.5m" data-testid="outro-medidas" />
                </div>
            </div>
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 flex gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                As fotografias são obrigatórias no passo seguinte para itens &ldquo;Outro&rdquo;.
            </div>
        </div>
    );
}

function StepConditions({ conditions, setConditions, disinfection, setDisinfection, impermeabilization, setImpermeabilization }) {
    const toggle = (k) => setConditions((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Estado e necessidades</h2>
            <p className="mt-2 text-white/60">Selecione tudo o que se aplica. Ajuda-nos a preparar produtos e tempo adequados.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
                <button
                    onClick={() => setConditions([])}
                    className="ab-service-tile text-left"
                    data-selected={conditions.length === 0 && !disinfection && !impermeabilization}
                    data-testid="cond-normal"
                >
                    <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#C9A961]" />
                        <div>
                            <div className="font-heading font-semibold">Estado normal</div>
                            <div className="text-sm text-white/50">Sem sujidade ou manchas especiais</div>
                        </div>
                    </div>
                </button>
                {CONDITIONS.map((c) => (
                    <button
                        key={c.key}
                        onClick={() => toggle(c.key)}
                        className="ab-service-tile text-left"
                        data-selected={conditions.includes(c.key)}
                        data-testid={`cond-${c.key}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded border-2 border-[#3A3A3A] flex items-center justify-center flex-shrink-0" style={{ background: conditions.includes(c.key) ? "#C9A961" : "transparent", borderColor: conditions.includes(c.key) ? "#C9A961" : "" }}>
                                {conditions.includes(c.key) && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <div className="font-medium">{c.label}</div>
                        </div>
                    </button>
                ))}
            </div>
            <div className="mt-8 space-y-4">
                <div className="p-6 rounded-none bg-[#C9A961]/10 border border-[#C9A961]/30">
                    <div className="flex items-start gap-3">
                        <Checkbox checked={disinfection} onCheckedChange={(v) => setDisinfection(!!v)} id="disinfect" data-testid="cond-disinfect" className="mt-1" />
                        <div>
                            <Label htmlFor="disinfect" className="font-heading font-bold text-lg cursor-pointer">Desinfeção profissional</Label>
                            <p className="text-sm text-white/60 mt-1">Produtos hospitalares certificados. Recomendado após presença de vírus, bactérias ou alérgenos.</p>
                        </div>
                    </div>
                </div>
                <div className="p-6 rounded-none bg-[#C9A961]/10 border border-[#C9A961]/30">
                    <div className="flex items-start gap-3">
                        <Checkbox checked={impermeabilization} onCheckedChange={(v) => setImpermeabilization(!!v)} id="imperm" data-testid="cond-imperm" className="mt-1" />
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Label htmlFor="imperm" className="font-heading font-bold text-lg cursor-pointer">Impermeabilização</Label>
                                <span className="text-[10px] uppercase tracking-widest font-bold bg-[#C9A961] text-black px-2 py-0.5 rounded">Sob Consulta</span>
                            </div>
                            <p className="text-sm text-white/60 mt-1">Proteção contra líquidos e nódoas. Valor confirmado após análise das fotografias e do tipo de tecido.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StepLocation({ location, setLocation }) {
    const upd = (patch) => setLocation({ ...location, ...patch });
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Onde é o serviço?</h2>
            <p className="mt-2 text-white/60">A taxa de deslocação depende da localização.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                    <Label>Código postal *</Label>
                    <Input placeholder="0000-000" value={location.postal_code} onChange={(e) => upd({ postal_code: e.target.value })} data-testid="loc-postal" />
                </div>
                <div>
                    <Label>Localidade *</Label>
                    <Input value={location.locality} onChange={(e) => upd({ locality: e.target.value })} data-testid="loc-locality" />
                </div>
                <div>
                    <Label>Distrito *</Label>
                    <Select value={location.district} onValueChange={(v) => upd({ district: v })}>
                        <SelectTrigger data-testid="loc-district"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                        <SelectContent>
                            {DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>                <div>
                    <Label>Andar</Label>
                    <Input value={location.floor} onChange={(e) => upd({ floor: e.target.value })} placeholder="R/C, 1º, 2º..." data-testid="loc-floor" />
                </div>
                <div className="sm:col-span-2">
                    <Label>Morada *</Label>
                    <Input value={location.address} onChange={(e) => upd({ address: e.target.value })} placeholder="Rua, número, andar" data-testid="loc-address" required />
                </div>
                <div>
                    <Label>Elevador</Label>
                    <RadioGroup value={location.elevator} onValueChange={(v) => upd({ elevator: v })} className="flex gap-4 pt-2" data-testid="loc-elevator">
                        <div className="flex items-center gap-2"><RadioGroupItem value="sim" id="el-s" /><Label htmlFor="el-s">Sim</Label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="nao" id="el-n" /><Label htmlFor="el-n">Não</Label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="nao_sei" id="el-ns" /><Label htmlFor="el-ns">Não sei</Label></div>
                    </RadioGroup>
                </div>
                <div>
                    <Label>Estacionamento</Label>
                    <RadioGroup value={location.parking} onValueChange={(v) => upd({ parking: v })} className="flex gap-4 pt-2" data-testid="loc-parking">
                        <div className="flex items-center gap-2"><RadioGroupItem value="facil" id="p-f" /><Label htmlFor="p-f">Fácil</Label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="dificil" id="p-d" /><Label htmlFor="p-d">Difícil</Label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="nao_sei" id="p-ns" /><Label htmlFor="p-ns">Não sei</Label></div>
                    </RadioGroup>
                </div>
            </div>
        </div>
    );
}

function StepPhotos({ photos, setPhotos, onUpload, uploading, required }) {
    const [drag, setDrag] = useState(false);
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Fotografias</h2>
            <p className="mt-2 text-white/60">{required ? "Obrigatório para o item 'Outro'." : "Opcional. Ajuda-nos a ser mais precisos."}</p>
            <div className="mt-8">
                <div
                    className="ab-upload-zone"
                    data-drag={drag}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDrag(false);
                        onUpload(Array.from(e.dataTransfer.files));
                    }}
                >
                    <Upload className="w-8 h-8 text-[#C9A961] mx-auto" />
                    <div className="mt-3 font-heading font-bold text-lg">Arraste fotografias ou clique para enviar</div>
                    <div className="text-sm text-white/50 mt-1">JPG, PNG, WEBP até 10MB cada</div>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        id="file-input"
                        onChange={(e) => onUpload(Array.from(e.target.files || []))}
                        data-testid="photos-input"
                    />
                    <label htmlFor="file-input" className="mt-4 inline-block cursor-pointer">
                        <span className="ab-btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center">
                            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A enviar…</> : "Escolher ficheiros"}
                        </span>
                    </label>
                </div>
                {photos.length > 0 && (
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {photos.map((p, i) => (
                            <div key={i} className="relative group aspect-square rounded-xl overflow-hidden border border-[#2A2A2A]">
                                <img src={fileUrl(p.path)} alt={p.name} className="w-full h-full object-cover" />
                                <button
                                    onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/80 border border-white/20 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white"
                                    data-testid={`photo-remove-${i}`}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function StepCustomer({ customer, setCustomer, preferredDate, setPreferredDate, preferredPeriod, setPreferredPeriod, notes, setNotes, promoCode, setPromoCode, privacyAccepted, setPrivacyAccepted }) {
    const upd = (patch) => setCustomer({ ...customer, ...patch });
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">Os seus dados</h2>
            <p className="mt-2 text-white/60">Para lhe podermos responder e agendar.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                    <Label>Nome completo *</Label>
                    <Input value={customer.name} onChange={(e) => upd({ name: e.target.value })} data-testid="cust-name" />
                </div>
                <div>
                    <Label>Telefone *</Label>
                    <Input value={customer.phone} onChange={(e) => upd({ phone: e.target.value })} data-testid="cust-phone" />
                </div>
                <div>
                    <Label>E-mail *</Label>
                    <Input type="email" value={customer.email} onChange={(e) => upd({ email: e.target.value })} data-testid="cust-email" />
                </div>
                <div>
                    <Label>Data preferida</Label>
                    <DatePickerPT value={preferredDate} onChange={setPreferredDate} />
                </div>
                <div>
                    <Label>Período preferido</Label>
                    <Select value={preferredPeriod} onValueChange={setPreferredPeriod}>
                        <SelectTrigger data-testid="cust-period"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="manha">Manhã</SelectItem>
                            <SelectItem value="tarde">Tarde</SelectItem>
                            <SelectItem value="indiferente">Indiferente</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-2">
                    <Label>Observações</Label>
                    <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cust-notes" />
                </div>
                <div className="sm:col-span-2">
                    <Label>Código promocional (opcional)</Label>
                    <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="Ex: BEMVINDO10" data-testid="cust-promo" />
                </div>
                <div className="sm:col-span-2 mt-2 flex items-start gap-3 p-4 bg-[#1F1F1F] rounded-lg border border-[#2A2A2A]">
                    <Checkbox checked={privacyAccepted} onCheckedChange={(v) => setPrivacyAccepted(!!v)} id="privacy" data-testid="cust-privacy" className="mt-1" />
                    <Label htmlFor="privacy" className="text-sm cursor-pointer leading-relaxed">
                        Aceito a política de privacidade e o tratamento dos meus dados pessoais para efeitos de resposta ao pedido de orçamento. *
                    </Label>
                </div>
            </div>
        </div>
    );
}

function DatePickerPT({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const selected = value ? parseISO(value) : undefined;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm text-left" data-testid="cust-date">
                    <span className={selected ? "text-white" : "text-white/40"}>{selected ? format(selected, "dd/MM/yyyy") : "dd/mm/aaaa"}</span>
                    <CalendarIcon className="w-4 h-4 text-[#C9A961]" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#141414] border-[#2A2A2A] text-white" align="start" data-testid="cust-date-calendar">
                <Calendar
                    mode="single"
                    locale={pt}
                    weekStartsOn={1}
                    selected={selected}
                    disabled={{ before: startOfDay(new Date()) }}
                    onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
                    classNames={{ day_selected: "bg-[#C9A961] text-black hover:bg-[#C9A961] focus:bg-[#C9A961]", day_today: "border border-[#C9A961]/60", caption_label: "text-sm font-medium capitalize" }}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}

function StepResult({ calc, onRecalc }) {
    if (!calc) {
        return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>;
    }
    return (
        <div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">A sua estimativa</h2>
            <p className="mt-2 text-white/60">Este valor é uma estimativa e está sujeito a confirmação após análise das informações e fotografias enviadas.</p>

            <div className="mt-8 ab-card p-8">
                <div className="space-y-3">
                    {calc.breakdown.map((b, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b last:border-b-0 border-[#2A2A2A]">
                            <div>
                                <div className="font-medium">{b.label}</div>
                                {calc.on_request_items.includes(b.label.split(" —")[0]) && <div className="text-xs text-amber-600">Sob avaliação</div>}
                            </div>
                            <div className="font-heading font-bold text-white">{b.price.toFixed(2)} €</div>
                        </div>
                    ))}
                    <div className="pt-3 space-y-2 text-sm">
                        <Row label="Subtotal" value={calc.subtotal} />
                        {calc.supplements > 0 && <Row label="Suplementos" value={calc.supplements} />}
                        {calc.travel_fee > 0 && <Row label={`Deslocação${calc.distance ? ` (${calc.distance.km} km)` : ""}`} value={calc.travel_fee} />}
                        {calc.discount > 0 && <Row label={`Desconto${calc.promo ? ` (${calc.promo.code})` : ""}`} value={-calc.discount} color="text-emerald-400" />}
                    </div>
                    <div className="pt-4 mt-2 border-t border-[#2A2A2A] flex items-baseline justify-between">
                        <div className="font-heading font-bold text-lg">Total estimado</div>
                        <div className="font-heading font-black text-3xl text-[#C9A961]" data-testid="result-total">{calc.total.toFixed(2)} €</div>
                    </div>
                    <div className="text-sm text-white/50">Duração aproximada: {Math.round(calc.duration_minutes / 60 * 10) / 10} horas</div>
                </div>
            </div>

            <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-4 flex gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Este valor é uma estimativa e está sujeito a confirmação após análise das informações e fotografias enviadas.</span>
            </div>
        </div>
    );
}

function Row({ label, value, color = "" }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-white/60">{label}</span>
            <span className={`font-medium ${color}`}>{value.toFixed(2)} €</span>
        </div>
    );
}

function SummaryCard({ selectedServices, calc, conditions, disinfection, impermeabilization }) {
    return (
        <div className="ab-card p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#C9A961] mb-3">Resumo</div>
            {selectedServices.length === 0 ? (
                <p className="text-white/50 text-sm">Selecione os serviços para ver a estimativa.</p>
            ) : (
                <div className="space-y-1">
                    {selectedServices.map((s) => (
                        <div key={s.key} className="text-sm text-white/70">• {s.name}</div>
                    ))}
                </div>
            )}
            {(conditions.length > 0 || disinfection || impermeabilization) && (
                <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
                    <div className="text-xs text-white/50 mb-1">Condições</div>
                    <div className="flex flex-wrap gap-1.5">
                        {conditions.map((c) => <span key={c} className="text-xs px-2 py-0.5 bg-[#1F1F1F] text-white/70 rounded-full">{c.replace(/_/g, " ")}</span>)}
                        {disinfection && <span className="text-xs px-2 py-0.5 bg-[#C9A961]/10 text-[#C9A961] rounded-full font-medium">+ desinfeção</span>}
                        {impermeabilization && <span className="text-xs px-2 py-0.5 bg-[#C9A961]/10 text-[#C9A961] rounded-full font-medium">+ impermeabilização</span>}
                    </div>
                </div>
            )}
            {calc && (
                <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
                    {calc.distance && (
                        <div className="text-xs text-white/50 mb-2">Distância: <b className="text-white">{calc.distance.km} km</b> (rota real)</div>
                    )}
                    <div className="flex items-baseline justify-between">
                        <span className="text-xs text-white/50">Estimativa</span>
                        <span className="font-heading font-black text-2xl text-[#C9A961]">{calc.total.toFixed(2)} €</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState({ msg }) {
    return <div className="ab-card p-10 text-center text-white/50">{msg}</div>;
}

function SubmittedView({ quote, onNew, onHome }) {
    const calc = quote.calculation || {};
    const customer = quote.customer || {};
    const whatsappMsg = encodeURIComponent(`Olá! Enviei o pedido de orçamento ${quote.reference} pelo site. Total estimado: ${calc.total?.toFixed(2)} €.`);
    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white py-10">
            <div className="max-w-3xl mx-auto px-6">
                <div className="ab-card p-8 lg:p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h1 className="mt-6 font-heading text-3xl lg:text-4xl font-bold tracking-tight">Pedido recebido!</h1>
                    <p className="mt-2 text-white/60">Referência: <span className="font-mono font-bold" data-testid="quote-reference">{quote.reference}</span></p>
                    <p className="mt-4 text-white/60 max-w-md mx-auto">Obrigado, {customer.name}! A nossa equipa vai analisar o pedido e entrar em contacto para confirmar o orçamento.</p>
                    <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
                        <a href={`https://wa.me/351967318921?text=${whatsappMsg}`} target="_blank" rel="noreferrer" data-testid="whatsapp-btn">
                            <Button variant="outline" className="w-full rounded-full h-12 border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                                <Phone className="w-4 h-4 mr-2" /> Confirmar por WhatsApp
                            </Button>
                        </a>
                        <a href={`mailto:altobrilho24@gmail.com?subject=Orçamento ${quote.reference}&body=Total estimado: ${calc.total?.toFixed(2)} €`} data-testid="email-btn">
                            <Button variant="outline" className="w-full rounded-full h-12">
                                <Mail className="w-4 h-4 mr-2" /> Receber por e-mail
                            </Button>
                        </a>
                    </div>
                    <div className="mt-8 pt-8 border-t border-[#2A2A2A]">
                        <div className="text-white/50 text-sm">Total estimado</div>
                        <div className="font-heading font-black text-5xl text-[#C9A961] mt-1" data-testid="submitted-total">{calc.total?.toFixed(2)} €</div>
                    </div>
                    <div className="mt-8 flex gap-3 justify-center">
                        <Button variant="ghost" onClick={onHome} data-testid="submitted-home">Voltar à página inicial</Button>
                        <Button onClick={onNew} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="submitted-new">Novo orçamento</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
